// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — useMyPAOrgData Hooks
// Filtered PA Grants data scoped to the user's organization
// Grantee orgs also see sub-applicant data (via parentOrgId)
// ═══════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useMyPAOrg } from './useMyPAOrg';
import { usePAGrantsStore } from '../store/paGrantsStore';

/** Returns org Rids the user can access. Grantees see sub-applicant orgs too. */
export function useMyPAOrgIds(): string[] {
  const myOrg = useMyPAOrg();
  const organizations = usePAGrantsStore(s => s.organizations);

  return useMemo(() => {
    if (!myOrg) return [];
    const myId = String(myOrg.Rid);
    // Grantees see their own org + all sub-applicants
    if (myOrg.orgRole === 'grantee') {
      const subIds = organizations
        .filter(o => String(o.parentOrgId) === myId)
        .map(o => String(o.Rid));
      return [myId, ...subIds];
    }
    return [myId];
  }, [myOrg, organizations]);
}

/** Projects belonging to the user's org(s) */
export function useMyPAProjects() {
  const orgIds = useMyPAOrgIds();
  const projects = usePAGrantsStore(s => s.projects);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return projects.filter(p => orgIds.includes(String(p.organizationId)));
  }, [orgIds, projects]);
}

/** Damage items belonging to the user's org(s) */
export function useMyPADamageItems() {
  const orgIds = useMyPAOrgIds();
  const damageItems = usePAGrantsStore(s => s.damageItems);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return damageItems.filter(d => orgIds.includes(String(d.organizationId)));
  }, [orgIds, damageItems]);
}

/** Documents belonging to the user's org(s) */
export function useMyPADocuments() {
  const orgIds = useMyPAOrgIds();
  const documents = usePAGrantsStore(s => s.documents);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return documents.filter(d => orgIds.includes(String(d.organizationId)));
  }, [orgIds, documents]);
}

/** Labor records belonging to the user's org(s) */
export function useMyPALabor() {
  const orgIds = useMyPAOrgIds();
  const laborRecords = usePAGrantsStore(s => s.laborRecords);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return laborRecords.filter(l => orgIds.includes(String(l.organizationId)));
  }, [orgIds, laborRecords]);
}

/** Equipment records belonging to the user's org(s) */
export function useMyPAEquipment() {
  const orgIds = useMyPAOrgIds();
  const equipRecords = usePAGrantsStore(s => s.equipRecords);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return equipRecords.filter(e => orgIds.includes(String(e.organizationId)));
  }, [orgIds, equipRecords]);
}

/** Materials belonging to the user's org(s) */
export function useMyPAMaterials() {
  const orgIds = useMyPAOrgIds();
  const materials = usePAGrantsStore(s => s.materials);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return materials.filter(m => orgIds.includes(String(m.organizationId)));
  }, [orgIds, materials]);
}

/** Contracts belonging to the user's org(s) */
export function useMyPAContracts() {
  const orgIds = useMyPAOrgIds();
  const contracts = usePAGrantsStore(s => s.contracts);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return contracts.filter(c => orgIds.includes(String(c.organizationId)));
  }, [orgIds, contracts]);
}

/** Rented equipment belonging to the user's org(s) */
export function useMyPARentedEquip() {
  const orgIds = useMyPAOrgIds();
  const rentedEquip = usePAGrantsStore(s => s.rentedEquip);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return rentedEquip.filter(r => orgIds.includes(String(r.organizationId)));
  }, [orgIds, rentedEquip]);
}

/** Donated resources belonging to the user's org(s) */
export function useMyPADonated() {
  const orgIds = useMyPAOrgIds();
  const donatedResources = usePAGrantsStore(s => s.donatedResources);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return donatedResources.filter(d => orgIds.includes(String(d.organizationId)));
  }, [orgIds, donatedResources]);
}

/** Insurance records belonging to the user's org(s) */
export function useMyPAInsurance() {
  const orgIds = useMyPAOrgIds();
  const insurance = usePAGrantsStore(s => s.insurance);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return insurance.filter(i => orgIds.includes(String(i.organizationId)));
  }, [orgIds, insurance]);
}

/** Compliance records belonging to the user's org(s) */
export function useMyPACompliance() {
  const orgIds = useMyPAOrgIds();
  const compliance = usePAGrantsStore(s => s.compliance);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return compliance.filter(c => orgIds.includes(String(c.organizationId)));
  }, [orgIds, compliance]);
}

/** Timeline events belonging to the user's org(s) */
export function useMyPATimeline() {
  const orgIds = useMyPAOrgIds();
  const timelineEvents = usePAGrantsStore(s => s.timelineEvents);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return timelineEvents.filter(t => orgIds.includes(String(t.organizationId)));
  }, [orgIds, timelineEvents]);
}

/** Notes belonging to the user's org(s) */
export function useMyPANotes() {
  const orgIds = useMyPAOrgIds();
  const notes = usePAGrantsStore(s => s.notes);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return notes.filter(n => orgIds.includes(String(n.organizationId)));
  }, [orgIds, notes]);
}

/** Time extension requests belonging to the user's org(s) */
export function useMyPATimeExtensions() {
  const orgIds = useMyPAOrgIds();
  const timeExtensions = usePAGrantsStore(s => s.timeExtensions);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return timeExtensions.filter(t => orgIds.includes(String(t.organizationId)));
  }, [orgIds, timeExtensions]);
}

/** Reimbursement requests belonging to the user's org(s) */
export function useMyPAReimbursements() {
  const orgIds = useMyPAOrgIds();
  const reimbursementRequests = usePAGrantsStore(s => s.reimbursementRequests);

  return useMemo(() => {
    if (!orgIds.length) return [];
    return reimbursementRequests.filter(r => orgIds.includes(String(r.organizationId)));
  }, [orgIds, reimbursementRequests]);
}
